import React from 'react';
import { Clock, Calendar, ChevronRight, ChevronDown, DollarSign, User, Edit } from 'lucide-react';
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';

interface PayrollEntry {
  id: string;
  resource_id: string;
  resource_first_name: string;
  resource_last_name: string;
  resource_email: string;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission' | 'bonus';
  hours: number;
  earnings: number;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  notes?: string;
  status?: string;
  process_status: 'PAID' | 'UNPAID';
  completed_at?: string;
}

interface PayrollResourceSummaryProps {
  resourceId: string;
  data: {
    resource: {
      first_name: string;
      last_name: string;
      email: string;
      compensation_type: string;
      flat_rate_amount: number | null;
      hourly_rate: number | null;
      avatar_url?: string;
    };
    timeclock: {
      entries: any[];
      totalHours: number;
      totalEarnings: number;
    };
    bookings: Record<string, {
      booking_number: number;
      booking_title: string;
      start_date: string;
      end_date: string;
      items: any[];
      totalHours: number;
      totalEarnings: number;
    }>;
    bonuses?: {
      entries: any[];
      totalEarnings: number;
    };
    totalHours: number;
    totalEarnings: number;
  };
  isExpanded: boolean;
  onToggle: () => void;
  onToggleBooking: (bookingId: string) => void;
  expandedBookings: Set<string>;
  onEditEntry: (entry: PayrollEntry) => void;
}

// Helper function to format duration
const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

// Helper function to safely format dates
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (!isValid(date)) return '-';
  return format(date, 'MMM d, yyyy');
};

// Helper function to safely format times
const formatTime = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (!isValid(date)) return '-';
  return format(date, 'h:mm a');
};

// Helper function to safely calculate elapsed time
const getElapsedTime = (startStr: string | null | undefined): string => {
  if (!startStr) return '-';
  const date = new Date(startStr);
  if (!isValid(date)) return '-';
  return formatDistanceToNow(date);
};

export default function PayrollResourceSummary({
  resourceId,
  data,
  isExpanded,
  onToggle,
  onToggleBooking,
  expandedBookings,
  onEditEntry
}: PayrollResourceSummaryProps) {
  return (
    <div className="transition-all duration-300">
      {/* Resource Header */}
      <div 
        className="group p-4 sm:p-6 lg:p-8 cursor-pointer transition-all duration-300 hover:bg-gradient-to-r hover:from-gray-50/30 hover:to-white/50"
        onClick={onToggle}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          {/* Avatar & Basic Info Row */}
          <div className="flex items-center gap-4 flex-1">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-sm sm:text-base lg:text-lg font-bold text-white overflow-hidden shadow-lg">
                {data.resource.avatar_url ? (
                  <img 
                    src={data.resource.avatar_url} 
                    alt={`${data.resource.first_name} ${data.resource.last_name}`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  `${data.resource.first_name[0]}${data.resource.last_name[0]}`
                )}
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
              </div>
            </div>

            {/* Resource Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                  {data.resource.first_name} {data.resource.last_name}
                </h3>
                <div className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium w-fit">
                  {data.resource.compensation_type.replace('_', ' ').toUpperCase()}
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 truncate">{data.resource.email}</p>
              
              {/* Total Earnings - Mobile */}
              <div className="sm:hidden mt-3 flex items-center justify-between">
                <div>
                  <div className="text-xl font-bold text-gray-900">
                    ${data.totalEarnings.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500">Total Earnings</div>
                </div>
                <div className="p-2 rounded-xl bg-white/60 backdrop-blur-sm border border-white/30 group-hover:bg-gray-50 transition-colors duration-200">
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                    isExpanded ? 'rotate-180' : ''
                  }`} />
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid - Hidden on Mobile, Shown on Tablet+ */}
          <div className="hidden sm:block">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 lg:gap-4 w-full">
              {/* Show Time and Clock stats only if there are timeclock entries */}
              {data.timeclock.entries.length > 0 && (
                <>
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl p-2 lg:p-3 border border-white/30">
                    <div className="flex items-center gap-1 lg:gap-2 mb-1">
                      <Clock className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">Time</span>
                    </div>
                    <div className="text-xs lg:text-sm font-bold text-gray-900">
                      {formatDuration(data.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
                    </div>
                  </div>
                  
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl p-2 lg:p-3 border border-white/30">
                    <div className="flex items-center gap-1 lg:gap-2 mb-1">
                      <DollarSign className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">Clock</span>
                    </div>
                    <div className="text-xs lg:text-sm font-bold text-gray-900">
                      ${data.timeclock.totalEarnings.toFixed(2)}
                    </div>
                  </div>
                </>
              )}
              
              {/* Show Flat Rate only if there are flat rate entries */}
              {Object.values(data.bookings).some(booking => 
                booking.items.some(item => item.entry_type === 'flat_rate' && item.earnings > 0)
              ) && (
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-2 lg:p-3 border border-white/30">
                  <div className="flex items-center gap-1 lg:gap-2 mb-1">
                    <DollarSign className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Flat</span>
                  </div>
                  <div className="text-xs lg:text-sm font-bold text-gray-900">
                    ${Object.values(data.bookings).reduce((sum, booking) => 
                      sum + booking.items.reduce((itemSum, item) => 
                        itemSum + (item.entry_type === 'flat_rate' ? item.earnings : 0), 
                        0
                      ), 0).toFixed(2)}
                  </div>
                </div>
              )}
              
              {/* Show Commission only if there are commission entries */}
              {Object.values(data.bookings).some(booking => 
                booking.items.some(item => item.entry_type === 'commission' && item.earnings > 0)
              ) && (
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-2 lg:p-3 border border-white/30">
                  <div className="flex items-center gap-1 lg:gap-2 mb-1">
                    <DollarSign className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Comm.</span>
                  </div>
                  <div className="text-xs lg:text-sm font-bold text-gray-900">
                    ${Object.values(data.bookings).reduce((sum, booking) => 
                      sum + booking.items.reduce((itemSum, item) => 
                        itemSum + (item.entry_type === 'commission' ? item.earnings : 0), 
                        0
                      ), 0).toFixed(2)}
                  </div>
                </div>
              )}
              
              {/* Show Bonus only if there are bonus entries */}
              {data.bonuses && data.bonuses.totalEarnings > 0 && (
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-2 lg:p-3 border border-white/30">
                  <div className="flex items-center gap-1 lg:gap-2 mb-1">
                    <DollarSign className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Bonus</span>
                  </div>
                  <div className="text-xs lg:text-sm font-bold text-gray-900">
                    ${(data.bonuses?.totalEarnings || 0).toFixed(2)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Total Earnings & Expand Button - Desktop */}
          <div className="hidden sm:flex items-center gap-4">
            <div className="text-right">
              <div className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">
                ${data.totalEarnings.toFixed(2)}
              </div>
              <div className="text-xs lg:text-sm text-gray-500">
                Total Earnings
              </div>
            </div>
            <div className="p-2 rounded-xl bg-white/60 backdrop-blur-sm border border-white/30 group-hover:bg-gray-50 transition-colors duration-200">
              <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`} />
            </div>
          </div>

          {/* Mobile Stats Grid - Shown when expanded on mobile */}
          {isExpanded && (
            <div className="sm:hidden mt-4 grid grid-cols-2 gap-3">
              {/* Show Time and Timeclock stats only if there are timeclock entries */}
              {data.timeclock.entries.length > 0 && (
                <>
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">Total Time</span>
                    </div>
                    <div className="text-sm font-bold text-gray-900">
                      {formatDuration(data.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
                    </div>
                  </div>
                  
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">Timeclock</span>
                    </div>
                    <div className="text-sm font-bold text-gray-900">
                      ${data.timeclock.totalEarnings.toFixed(2)}
                    </div>
                  </div>
                </>
              )}
              
              {/* Show Flat Rate only if there are flat rate entries */}
              {Object.values(data.bookings).some(booking => 
                booking.items.some(item => item.entry_type === 'flat_rate' && item.earnings > 0)
              ) && (
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Flat Rate</span>
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    ${Object.values(data.bookings).reduce((sum, booking) => 
                      sum + booking.items.reduce((itemSum, item) => 
                        itemSum + (item.entry_type === 'flat_rate' ? item.earnings : 0), 
                        0
                      ), 0).toFixed(2)}
                  </div>
                </div>
              )}
              
              {/* Show Commission only if there are commission entries */}
              {Object.values(data.bookings).some(booking => 
                booking.items.some(item => item.entry_type === 'commission' && item.earnings > 0)
              ) && (
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Commission</span>
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    ${Object.values(data.bookings).reduce((sum, booking) => 
                      sum + booking.items.reduce((itemSum, item) => 
                        itemSum + (item.entry_type === 'commission' ? item.earnings : 0), 
                        0
                      ), 0).toFixed(2)}
                  </div>
                </div>
              )}
              
              {/* Show Bonus only if there are bonus entries */}
              {data.bonuses && data.bonuses.totalEarnings > 0 && (
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Bonus</span>
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    ${(data.bonuses?.totalEarnings || 0).toFixed(2)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="bg-gradient-to-r from-gray-50/50 to-white/30 border-t border-gray-100/50">
          {/* Timeclock Entries */}
                      {data.timeclock.entries.length > 0 && (
             <div className="p-4 sm:p-6 border-b border-gray-100/50">
               <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                 <div className="flex items-center gap-3">
                   <div className="p-2 bg-gray-500/10 rounded-xl">
                     <Clock className="h-5 w-5 text-gray-600" />
                   </div>
                   <div>
                     <h3 className="text-base sm:text-lg font-semibold text-gray-900">Time Clock Entries</h3>
                     <p className="text-xs sm:text-sm text-gray-600">
                       ${data.timeclock.totalEarnings.toFixed(2)} total earnings
                     </p>
                   </div>
                 </div>
                 <div className="text-xs sm:text-sm font-medium text-gray-500">
                   {formatDuration(data.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
                 </div>
               </div>
              
              <div className="grid gap-4">
                {data.timeclock.entries.map((entry) => (
                  <div key={entry.id} className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-white/30 shadow-sm hover:shadow-md transition-all duration-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {format(new Date(entry.clock_in), 'MMM d, yyyy')}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            entry.process_status === 'PAID' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {entry.process_status === 'PAID' ? 'PAID' : 'UNPAID'}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm text-gray-600">
                          <span>
                            {format(new Date(entry.clock_in), 'h:mm a')} - {
                              entry.clock_out ? format(new Date(entry.clock_out), 'h:mm a') : 'In Progress'
                            }
                          </span>
                        </div>
                        
                        {entry.break_start && (
                          <div className="mt-2 text-xs text-gray-500">
                            Break: {format(new Date(entry.break_start), 'h:mm a')} - {
                              entry.break_end ? format(new Date(entry.break_end), 'h:mm a') : 'In Progress'
                            }
                          </div>
                        )}
                      </div>
                      
                                             <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-900 mb-1">
                            {formatDuration(entry.total_minutes || 0)}
                          </div>
                          <div className="text-sm font-semibold text-gray-700">
                            ${((entry.total_minutes || 0) / 60 * (data.resource.hourly_rate || 0)).toFixed(2)}
                          </div>
                        </div>
                        
                        {/* Edit Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditEntry({
                              ...entry,
                              resource_first_name: data.resource.first_name,
                              resource_last_name: data.resource.last_name,
                              resource_email: data.resource.email,
                              compensation_type: data.resource.compensation_type,
                              flat_rate_amount: data.resource.flat_rate_amount,
                              hourly_rate: data.resource.hourly_rate,
                              entry_type: 'timeclock',
                              earnings: (entry.total_minutes || 0) / 60 * (data.resource.hourly_rate || 0)
                            });
                          }}
                          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-150"
                          title="Edit time entry"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bonus Entries */}
          {data.bonuses && data.bonuses.entries.length > 0 && (
            <div className="p-4 sm:p-6 border-b border-gray-100/50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-xl">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Booking Bonuses</h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      ${data.bonuses.totalEarnings.toFixed(2)} total bonus earnings
                    </p>
                  </div>
                </div>
                <div className="text-xs sm:text-sm font-medium text-gray-500">
                  {data.bonuses.entries.length} bonus{data.bonuses.entries.length !== 1 ? 'es' : ''}
                </div>
              </div>
             
             <div className="grid gap-4">
               {data.bonuses.entries.map((entry) => (
                 <div key={entry.id} className="bg-gradient-to-r from-green-50/70 to-emerald-50/70 backdrop-blur-sm rounded-2xl p-4 border border-green-200/30 shadow-sm hover:shadow-md transition-all duration-200">
                   <div className="flex items-center justify-between">
                     <div className="flex-1">
                       <div className="flex items-center gap-3 mb-2">
                         <Calendar className="h-4 w-4 text-green-400" />
                         <span className="text-sm font-medium text-gray-900">
                           {formatDate(entry.created_at)}
                         </span>
                         <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                           BONUS
                         </span>
                       </div>
                       
                       <div className="flex items-center justify-between text-sm text-gray-600">
                         <span>
                           {entry.item_name || 'Booking Creation Bonus'}
                         </span>
                       </div>
                       
                       {entry.item_description && (
                         <div className="mt-2 text-xs text-gray-500">
                           {entry.item_description}
                         </div>
                       )}
                       
                       {entry.booking_number && (
                         <div className="mt-2 text-xs text-gray-500">
                           For Booking #{entry.booking_number}
                         </div>
                       )}
                     </div>
                     
                     <div className="flex items-center gap-3">
                       <div className="text-right">
                         <div className="text-lg font-bold text-green-600 mb-1">
                           ${(entry.earnings || 0).toFixed(2)}
                         </div>
                         <div className="text-xs text-gray-500">
                           Bonus Earned
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               ))}
             </div>
           </div>
          )}

          {/* Booking Entries */}
          {Object.entries(data.bookings).map(([bookingId, booking]) => (
            <div key={bookingId} className="border-b border-gray-100/50 last:border-b-0">
              <div 
                className="px-4 sm:px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-white/30 transition-colors duration-200"
                onClick={() => onToggleBooking(`${resourceId}-${bookingId}`)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="p-2 bg-gray-500/10 rounded-xl flex-shrink-0">
                    <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm sm:text-base text-gray-900 truncate">
                      Job #{booking.booking_number} - {booking.booking_title}
                    </div>
                    {booking.end_date && (
                      <div className="text-xs sm:text-sm text-gray-500 mt-1">
                        Completed {format(parseISO(booking.end_date), 'MMM d, yyyy')}
                      </div>
                    )}
                    <div className="text-xs sm:text-sm text-gray-600 mt-1">
                      {booking.totalHours.toFixed(1)}h • ${booking.totalEarnings.toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="p-2 rounded-xl bg-white/60 backdrop-blur-sm border border-white/30 flex-shrink-0">
                  <ChevronDown 
                    className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform duration-200 ${
                      expandedBookings.has(`${resourceId}-${bookingId}`) ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </div>
              
              {expandedBookings.has(`${resourceId}-${bookingId}`) && (
                <div className="bg-white/40 backdrop-blur-sm">
                  {/* Desktop Table */}
                  <div className="hidden lg:block overflow-hidden">
                    <table className="min-w-full">
                      <thead className="bg-white/60 backdrop-blur-sm">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Item
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Hours
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Rate
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Total
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100/50">
                        {booking.items.map((item) => (
                          <tr key={item.id} className="hover:bg-white/60 transition-colors duration-150">
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">{item.item_name}</div>
                              {item.item_description && (
                                <div className="text-sm text-gray-500 mt-1">{item.item_description}</div>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {formatDate(item.completed_at || item.created_at)}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {item.hours.toFixed(1)}h
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {item.entry_type === 'commission' 
                                ? 'Commission'
                                : `$${item.compensation_type === 'flat_rate' 
                                  ? item.flat_rate_amount?.toFixed(2)
                                  : item.hourly_rate?.toFixed(2)
                                }/hr`
                              }
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-bold text-gray-900">
                                  ${(() => {
                                    if (item.entry_type === 'flat_rate') {
                                      return ((item.hours || 0) * (item.flat_rate_amount || 0)).toFixed(2);
                                    } else if (item.entry_type === 'commission') {
                                      return (item.earnings || 0).toFixed(2);
                                    }
                                    return '0.00';
                                  })()}
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  item.process_status === 'PAID' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {item.process_status === 'PAID' ? 'PAID' : 'UNPAID'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditEntry({
                                    ...item,
                                    resource_id: resourceId,
                                    resource_first_name: data.resource.first_name,
                                    resource_last_name: data.resource.last_name,
                                    resource_email: data.resource.email,
                                    compensation_type: data.resource.compensation_type,
                                    flat_rate_amount: data.resource.flat_rate_amount,
                                    hourly_rate: data.resource.hourly_rate,
                                    booking_number: booking.booking_number,
                                    booking_title: booking.booking_title,
                                    start_date: booking.start_date,
                                    end_date: booking.end_date
                                  });
                                }}
                                className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-150"
                                title="Edit entry"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile/Tablet Cards */}
                  <div className="lg:hidden p-4 space-y-3">
                    {booking.items.map((item) => (
                      <div key={item.id} className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 text-sm truncate">{item.item_name}</h4>
                            {item.item_description && (
                              <p className="text-xs text-gray-500 mt-1">{item.item_description}</p>
                            )}
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ml-3 ${
                            item.process_status === 'PAID' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {item.process_status === 'PAID' ? 'PAID' : 'UNPAID'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <span className="text-gray-500">Date:</span>
                            <div className="font-medium text-gray-900">
                              {formatDate(item.completed_at || item.created_at)}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Hours:</span>
                            <div className="font-medium text-gray-900">{item.hours.toFixed(1)}h</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Rate:</span>
                            <div className="font-medium text-gray-900">
                              {item.entry_type === 'commission' 
                                ? 'Commission'
                                : `$${item.compensation_type === 'flat_rate' 
                                  ? item.flat_rate_amount?.toFixed(2)
                                  : item.hourly_rate?.toFixed(2)
                                }/hr`
                              }
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Total:</span>
                            <div className="font-bold text-gray-900">
                              ${(() => {
                                if (item.entry_type === 'flat_rate') {
                                  return ((item.hours || 0) * (item.flat_rate_amount || 0)).toFixed(2);
                                } else if (item.entry_type === 'commission') {
                                  return (item.earnings || 0).toFixed(2);
                                }
                                return '0.00';
                              })()}
                            </div>
                          </div>
                        </div>
                        
                        {/* Edit Button for Mobile */}
                        <div className="mt-3 pt-3 border-t border-gray-100/50">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditEntry({
                                ...item,
                                resource_id: resourceId,
                                resource_first_name: data.resource.first_name,
                                resource_last_name: data.resource.last_name,
                                resource_email: data.resource.email,
                                compensation_type: data.resource.compensation_type,
                                flat_rate_amount: data.resource.flat_rate_amount,
                                hourly_rate: data.resource.hourly_rate,
                                booking_number: booking.booking_number,
                                booking_title: booking.booking_title,
                                start_date: booking.start_date,
                                end_date: booking.end_date
                              });
                            }}
                            className="w-full flex items-center justify-center gap-2 p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-150 text-sm"
                          >
                            <Edit className="h-4 w-4" />
                            Edit Entry
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}